import * as Constants from './utils/constants';
import doubleClickZoom from './utils/double_click_zoom';
import createSupplementaryPoints from './utils/create_supplementary_points';
import * as CommonSelectors from './utils/common_selectors';
import moveFeatures from './utils/move_features';

import { lineString, point } from '@turf/helpers';
import bearing from '@turf/bearing';
import center from '@turf/center';
import midpoint from '@turf/midpoint';
import distance from '@turf/distance';
import destination from '@turf/destination';
import transformRotate from '@turf/transform-rotate';
import transformScale from '@turf/transform-scale';
import turfBbox from '@turf/bbox';
import { drawModes, drawState, Source } from '../../Types';
import { GeoJSONFeature } from 'maplibre-gl';
import { ensureDrawTransformIcons } from './utils/drawTransformIcons';

/** Axis-aligned bbox corners: top-left, top-right, bottom-right, bottom-left. */
function getBboxCorners(geojson: GeoJSONFeature): number[][] {
  const [minX, minY, maxX, maxY] = turfBbox(geojson as any);
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return [];

  let x0 = minX;
  let y0 = minY;
  let x1 = maxX;
  let y1 = maxY;
  const minSpan = 0.00003;
  if (x1 - x0 < minSpan) {
    const c = (x0 + x1) / 2;
    x0 = c - minSpan / 2;
    x1 = c + minSpan / 2;
  }
  if (y1 - y0 < minSpan) {
    const c = (y0 + y1) / 2;
    y0 = c - minSpan / 2;
    y1 = c + minSpan / 2;
  }

  return [
    [x0, y1],
    [x1, y1],
    [x1, y0],
    [x0, y0],
  ];
}

function bboxCornerFeatures(featureId: string, corners: number[][]) {
  return corners.map((coord, index) => ({
    type: Constants.geojsonTypes.FEATURE,
    properties: {
      meta: Constants.meta.BBOX_CORNER,
      parent: featureId,
      coord_path: String(index),
    },
    geometry: {
      type: Constants.geojsonTypes.POINT,
      coordinates: coord,
    },
  }));
}

function getDrawFeatureId(geojson: GeoJSONFeature): string | null {
  const id = geojson.id ?? geojson.properties?.id ?? geojson.properties?.__mapSketchId;
  return id == null ? null : String(id);
}

// import scalePNG from "../../img/scale_object.png";
// import rotatePNG from "../../img/rotate_object.png";

const SRMode:drawModes = <drawModes>{}; // scale rotate mode

export const SRCenter = {
  Center: 0, // rotate or scale around center of polygon
  Opposite: 1, // rotate or scale around opposite side of polygon
};


function parseSRCenter(value:any, defaultSRCenter = SRCenter.Center) {
  if (value == undefined || value == null) return defaultSRCenter;

  if (value === SRCenter.Center || value === SRCenter.Opposite) return value;

  if (value == 'center') return SRCenter.Center;

  if (value == 'opposite') return SRCenter.Opposite;

  throw Error(`Invalid SRCenter: ${value}`);
}


SRMode.onSetup = function (opts:any) {
  const snapSrc = this.map.getSource('geobox-gl-snap-marker');
  if (snapSrc) {
    const snapMarkerSource = (<Source>snapSrc)._data;
    snapMarkerSource.features = [{
      "type": "Feature",
      "properties": {},
      "geometry": {
        "type": "Point",
        "coordinates": []
      }
    }];
    (<Source>snapSrc).setData(snapMarkerSource);
  }

  const selected = this.getSelected?.() ?? [];
  const featureId = opts?.featureId ?? selected[0]?.id;
  if (featureId == null) {
    throw new Error('You must provide a valid featureId to enter SRMode');
  }

  const feature = this.getFeature(featureId);

  if (!feature) {
    throw new Error('You must provide a valid featureId to enter SRMode');
  }

  if (
    feature.type === Constants.geojsonTypes.POINT
    || feature.type === Constants.geojsonTypes.MULTI_POINT
  ) {
    throw new TypeError('SRMode cannot handle points');
  }

  const state = {
    featureId,
    feature,

    canTrash: opts.canTrash != undefined ? opts.canTrash : true,

    canScale: opts.canScale != undefined ? opts.canScale : false,
    canRotate: opts.canRotate != undefined ? opts.canRotate : false,

    rotationPointRadius:
      opts.rotationPointRadius != undefined ? opts.rotationPointRadius : 1.2,

    rotatePivot: parseSRCenter(opts.rotatePivot, SRCenter.Center),
    scaleCenter: parseSRCenter(
      opts.scaleCenter,
      opts.useBboxHandles !== false ? SRCenter.Opposite : SRCenter.Center
    ),

    useBboxHandles: opts.useBboxHandles !== false,
    canSelectFeatures:
      opts.canSelectFeatures !== undefined
        ? opts.canSelectFeatures
        : opts.useBboxHandles === false,
    /** When true, only the edited feature is drawn (others stay on the app layer). */
    hideUnselectedFeatures: opts.hideUnselectedFeatures === true,
    singleRotationPoint:
      opts.singleRotationPoint !== undefined ? opts.singleRotationPoint : true,

    dragMoveLocation: opts.startPos || null,
    dragMoving: false,
    canDragMove: false,
    selectedCoordPaths: opts.coordPath ? [opts.coordPath] : [],
  };

  if (!(state.canRotate || state.canScale)) {
    console.warn('Non of canScale or canRotate is true');
  }

  this.setSelectedCoordinates(
    this.pathsToCoordinates(featureId, state.selectedCoordPaths),
  );
  this.setSelected(featureId);
  doubleClickZoom.disable(this);
  ensureDrawTransformIcons(this.map);

  this.setActionableState({
    combineFeatures: false,
    uncombineFeatures: false,
    trash: state.canTrash,
  });

  // const _this = this;
  
  // if (!this.map.hasImage('rotate')) {
  //   let image = await this.map.loadImage(rotatePNG);
  //   this.map.addImage("rotate", image.data, { sdf: true });

  //   // this.map.loadImage(rotatePNG, (error, image) => {
  //   //   if (error) throw error;
  //   //   _this.map.addImage('rotate', image);
  //   // });
  // }

  // if (!this.map.hasImage('scale')) {
  //   let image = await this.map.loadImage(scalePNG);
  //   this.map.addImage("scale", image.data, { sdf: true });

  //   // this.map.loadImage(scalePNG, (error, image) => {
  //   //   if (error) throw error;
  //   //   _this.map.addImage('scale', image);
  //   // });
  // }
  return state;
};

SRMode.toDisplayFeatures = function (state:drawState, geojson:GeoJSONFeature, push:any) {
  if (String(state.featureId) !== getDrawFeatureId(geojson)) {
    if (state.hideUnselectedFeatures) {
      // Keep other store features out of the draw canvas so they stay on the
      // app display layer and do not pick up draw active/inactive styling.
      return;
    }
    geojson.properties.active = Constants.activeStates.INACTIVE;
    push(geojson);
    this.setActionableState({
      combineFeatures: false,
      uncombineFeatures: false,
      trash: state.canTrash,
    });
    return;
  }

  geojson.properties.active = Constants.activeStates.ACTIVE;
  push(geojson);

  const featureId = getDrawFeatureId(geojson) ?? String(state.featureId);

  if (state.useBboxHandles) {
    const corners = getBboxCorners(geojson);
    if (corners.length === 4) {
      push({
        type: Constants.geojsonTypes.FEATURE,
        properties: {
          meta: Constants.meta.BBOX_FRAME,
          parent: featureId,
          active: Constants.activeStates.ACTIVE,
        },
        geometry: {
          type: Constants.geojsonTypes.LINE_STRING,
          coordinates: [...corners, corners[0]],
        },
      });

      const cornerPoints = bboxCornerFeatures(featureId, corners);

      if (state.canRotate) {
        const rotPoints = this.createRotationPoints(state, geojson, cornerPoints);
        rotPoints.forEach(push);
      }

      if (state.canScale) {
        cornerPoints.forEach(push);
      }
    }
  } else {
    const suppPoints = createSupplementaryPoints(geojson, {
      map: this.map,
      midpoints: false,
      selectedPaths: state.selectedCoordPaths,
    });

    if (state.canRotate) {
      const rotPoints = this.createRotationPoints(state, geojson, suppPoints);
      rotPoints.forEach(push);
    }

    if (state.canScale) {
      const geomType = String(geojson.geometry?.type ?? '');
      if (geomType.includes('Polygon') || geomType.includes('Line')) {
        suppPoints.forEach(push);
      }
    }
  }

  // this.fireActionable(state);
  this.setActionableState({
    combineFeatures: false,
    uncombineFeatures: false,
    trash: state.canTrash,
  });

  // this.fireUpdate();
};

SRMode.onStop = function () {
  doubleClickZoom.enable(this);
  this.clearSelectedCoordinates();
};

// TODO why I need this?
SRMode.pathsToCoordinates = function (featureId:string, paths:[]) {
  return paths.map((coord_path) => ({ feature_id: featureId, coord_path }));
};

SRMode.computeBisectrix = function (points:any) {
  for (let i1 = 0; i1 < points.length; i1++) {
    const i0 = (i1 - 1 + points.length) % points.length;
    const i2 = (i1 + 1) % points.length;

    const l1 = lineString([
      points[i0].geometry.coordinates,
      points[i1].geometry.coordinates,
    ]);
    const l2 = lineString([
      points[i1].geometry.coordinates,
      points[i2].geometry.coordinates,
    ]);
    const a1 = bearing(
      points[i0].geometry.coordinates,
      points[i1].geometry.coordinates,
    );
    const a2 = bearing(
      points[i2].geometry.coordinates,
      points[i1].geometry.coordinates,
    );

    let a = (a1 + a2) / 2.0;

    if (a < 0.0) a += 360;
    if (a > 360) a -= 360;

    points[i1].properties.heading = a;
  }
};

SRMode.createRotationPoint = function (
  rotationWidgets:any,
  featureId:string,
  v1:any,
  v2:any,
  rotCenter:[],
  radiusScale:number,
) {

  const cR0 = midpoint(v1, v2).geometry.coordinates;
  const heading = bearing(rotCenter, cR0);
  const distance0 = distance(rotCenter, cR0);
  const distance1 = radiusScale * distance0; // TODO depends on map scale

  const cR1 = destination(rotCenter, distance1, heading, {}).geometry.coordinates;

  rotationWidgets.push({
    type: Constants.geojsonTypes.FEATURE,
    properties: {
      meta: Constants.meta.MIDPOINT,
      icon: 'rotate',
      parent: featureId,
      lng: cR1[0],
      lat: cR1[1],
      coord_path: v1.properties.coord_path,
      heading,
    },
    geometry: {
      type: Constants.geojsonTypes.POINT,
      coordinates: cR1,
    },
  });
};

SRMode.createRotationPoints = function (state:drawState, geojson:GeoJSONFeature, suppPoints:any) {

  const { type } = geojson.geometry;
  const featureId = geojson.properties && geojson.properties.id;

  const rotationWidgets:any[] = [];
  if (
    type === Constants.geojsonTypes.POINT
    || type === Constants.geojsonTypes.MULTI_POINT
  ) {
    return;
  }

  const corners = suppPoints.slice(0);
  corners[corners.length] = corners[0];

  let v1:any;

  const rotCenter = this.computeRotationCenter(state, geojson);

  if (state.singleRotationPoint) {
    let secondPoint
    if (!corners[3]) {
      secondPoint = corners[0]
    } else {
      secondPoint = corners[1]
    }
    this.createRotationPoint(
      rotationWidgets,
      featureId,
      corners[0],
      secondPoint,
      rotCenter,
      state.rotationPointRadius,
    );
  } else {
    corners.forEach((v2:any) => {
      if (v1 != null) {
        this.createRotationPoint(
          rotationWidgets,
          featureId,
          v1,
          v2,
          rotCenter,
          state.rotationPointRadius,
        );
      }

      v1 = v2;
    });
  }

  return rotationWidgets;
};

// SRMode._createScalePoint = function (
//   scaleWidgets,
//   featureId,
//   v1,
//   v2,
//   // rotCenter,
//   radiusScale,
// ) {
//   const cR0 = midpoint(v1, v2).geometry.coordinates;
//   // const heading = bearing(rotCenter, cR0);
//   // const distance0 = distance(rotCenter, cR0);
//   const distance1 = radiusScale * distance0; // TODO depends on map scale
//   const cR1 = destination(rotCenter, distance1, heading, {}).geometry.coordinates;

//   rotationWidgets.push({
//     type: Constants.geojsonTypes.FEATURE,
//     properties: {
//       meta: Constants.meta.MIDPOINT,
//       icon: 'rotate',
//       parent: featureId,
//       lng: cR1[0],
//       lat: cR1[1],
//       coord_path: v1.properties.coord_path,
//       heading,
//     },
//     geometry: {
//       type: Constants.geojsonTypes.POINT,
//       coordinates: cR1,
//     },
//   });
// };

// SRMode.createScalePoints = function (state, geojson, suppPoints) {
//   const { type, coordinates } = geojson.geometry;
//   const featureId = geojson.properties && geojson.properties.id;

//   const scaleWidgets = [];
//   if (
//     type === Constants.geojsonTypes.POINT
//     || type === Constants.geojsonTypes.MULTI_POINT
//   ) {
//     return;
//   }

//   const corners = suppPoints.slice(0);
//   corners[corners.length] = corners[0];

//   let v1 = null;

//   const rotCenter = this.computeRotationCenter(state, geojson);

//   if (state.singleRotationPoint) {
//     this._createRotationPoint(
//       rotationWidgets,
//       featureId,
//       corners[0],
//       corners[1],
//       rotCenter,
//       state.rotationPointRadius,
//     );
//   } else {
//     corners.forEach((v2) => {
//       if (v1 != null) {
//         this._createRotationPoint(
//           rotationWidgets,
//           featureId,
//           v1,
//           v2,
//           rotCenter,
//           state.rotationPointRadius,
//         );
//       }

//       v1 = v2;
//     });
//   }
//   return rotationWidgets;
// };

SRMode.startDragging = function (state:drawState, e:any) {

  this.map.dragPan.disable();
  state.canDragMove = true;
  state.dragMoveLocation = e.lngLat;
};

SRMode.stopDragging = function (state:drawState) {

  this.map.dragPan.enable();
  state.dragMoving = false;
  state.canDragMove = false;
  state.dragMoveLocation = <drawState["dragMoveLocation"]>{};
};

const isVertex = CommonSelectors.isOfMetaType(Constants.meta.VERTEX);

function isBboxCorner(e: any) {
  return e?.featureTarget?.properties?.meta === Constants.meta.BBOX_CORNER;
}

function isRotatePoint(e: any) {
  return (
    CommonSelectors.isOfMetaType(Constants.meta.MIDPOINT)(e)
    && e?.featureTarget?.properties?.icon === 'rotate'
  );
}

const BBOX_HANDLE_HIT_PX = 18;

/** Screen-distance hit test — reliable when the feature polygon wins layer queries. */
function pickBboxHandleByProximity(map: any, state: drawState, e: any) {
  const pt = e?.point;
  if (!pt || !state?.feature) return null;

  const geojson = state.feature.toGeoJSON();
  const cornerCoords = getBboxCorners(geojson);
  if (cornerCoords.length !== 4) return null;

  let bestCorner = -1;
  let bestDist = Infinity;

  for (let i = 0; i < cornerCoords.length; i++) {
    const screen = map.project(cornerCoords[i]);
    const d = Math.hypot(screen.x - pt.x, screen.y - pt.y);
    if (d < BBOX_HANDLE_HIT_PX && d < bestDist) {
      bestDist = d;
      bestCorner = i;
    }
  }

  if (bestCorner >= 0 && state.canScale) {
    return {
      kind: 'corner' as const,
      feature: {
        properties: {
          meta: Constants.meta.BBOX_CORNER,
          coord_path: String(bestCorner),
        },
      },
    };
  }

  if (state.canRotate) {
    const featureId = String(geojson.properties?.id ?? state.featureId);
    const cornerPoints = bboxCornerFeatures(featureId, cornerCoords);
    const rotPoints = SRMode.createRotationPoints(state, geojson, cornerPoints);
    const rot = rotPoints?.[0];
    const rotCoord = rot?.geometry?.coordinates;
    if (rotCoord) {
      const screen = map.project(rotCoord);
      const d = Math.hypot(screen.x - pt.x, screen.y - pt.y);
      if (d < BBOX_HANDLE_HIT_PX) {
        return { kind: 'rotate' as const, feature: rot };
      }
    }
  }

  return null;
}

/** Layer query fallback for bbox handles. */
function pickBboxTransformHandle(map: any, e: any) {
  const pt = e?.point;
  if (!pt || typeof map?.queryRenderedFeatures !== 'function') return null;

  let hits: any[] = [];
  try {
    hits =
      map.queryRenderedFeatures(
        [
          [pt.x - 14, pt.y - 14],
          [pt.x + 14, pt.y + 14],
        ],
        { validate: false }
      ) ?? [];
  } catch {
    return null;
  }

  const corner = hits.find((f) => f.properties?.meta === Constants.meta.BBOX_CORNER);
  if (corner) return { kind: 'corner' as const, feature: corner };

  const rotate = hits.find(
    (f) =>
      f.properties?.meta === Constants.meta.MIDPOINT &&
      f.properties?.icon === 'rotate'
  );
  if (rotate) return { kind: 'rotate' as const, feature: rotate };

  return null;
}

SRMode.onTouchStart = SRMode.onMouseDown = function (state:drawState, e:any) {

  if (state.useBboxHandles) {
    const picked =
      pickBboxHandleByProximity(this.map, state, e)
      ?? pickBboxTransformHandle(this.map, e);
    if (picked?.kind === 'corner' && state.canScale) {
      return this.onVertex(state, { ...e, featureTarget: picked.feature });
    }
    if (picked?.kind === 'rotate' && state.canRotate) {
      return this.onRotatePoint(state, { ...e, featureTarget: picked.feature });
    }
  }

  if (state.canScale && (isBboxCorner(e) || (!state.useBboxHandles && isVertex(e)))) {
    return this.onVertex(state, e);
  }
  if (state.canRotate && isRotatePoint(e)) return this.onRotatePoint(state, e);
  if (CommonSelectors.isActiveFeature(e)) return this.onFeature(state, e);
  // if (isMidpoint(e)) return this.onMidpoint(state, e);
  return this.clickNoTarget(state, e);
};

const TxMode = {
  Scale: 1,
  Rotate: 2,
};

SRMode.onVertex = function (state:drawState, e:any) {

  // convert internal MapboxDraw feature to valid GeoJSON:
  this.computeAxes(state, state.feature.toGeoJSON());

  this.startDragging(state, e);
  const about = e.featureTarget.properties;
  state.selectedCoordPaths = [about.coord_path];
  state.txMode = TxMode.Scale;
};

SRMode.onRotatePoint = function (state:drawState, e:any) {
  // convert internal MapboxDraw feature to valid GeoJSON:
  this.computeAxes(state, state.feature.toGeoJSON());

  this.startDragging(state, e);
  const about = e.featureTarget.properties;
  state.selectedCoordPaths = [about.coord_path];
  state.txMode = TxMode.Rotate;
};

SRMode.onFeature = function (state:drawState, e:any) {
  state.selectedCoordPaths = [];
  this.startDragging(state, e);
};

SRMode.coordinateIndex = function (coordPaths:any[]) {
  if (coordPaths.length >= 1) {
    const parts = coordPaths[0].split('.');
    return parseInt(parts[parts.length - 1]);
  }
  return 0;

};

SRMode.computeRotationCenter = function (state:drawState, polygon:any) {
  const center0 = center(polygon);
  return center0;
};

SRMode.resolveTransformCorners = function (state: drawState, polygon: any): number[][] {
  if (state.useBboxHandles) {
    return getBboxCorners(polygon);
  }

  let corners: number[][] = [];
  if (polygon.geometry.type === Constants.geojsonTypes.POLYGON) {
    corners = polygon.geometry.coordinates[0].slice(0);
  } else if (polygon.geometry.type === Constants.geojsonTypes.MULTI_POLYGON) {
    const temp: number[][] = [];
    polygon.geometry.coordinates.forEach((c: any) => {
      c.forEach((c2: any) => {
        c2.forEach((c3: any) => {
          temp.push(c3);
        });
      });
    });
    corners = temp;
  } else if (polygon.geometry.type === Constants.geojsonTypes.LINE_STRING) {
    corners = polygon.geometry.coordinates;
  } else if (polygon.geometry.type === Constants.geojsonTypes.MULTI_LINE_STRING) {
    const temp: number[][] = [];
    polygon.geometry.coordinates.forEach((c: any) => {
      c.forEach((c2: any) => {
        temp.push(c2);
      });
    });
    corners = temp;
  }
  return corners;
};

SRMode.computeAxes = function (state:drawState, polygon:any) {
  const center0 = this.computeRotationCenter(state, polygon);
  const corners = this.resolveTransformCorners(state, polygon);
  if (!corners || corners.length < 2) return;

  const n = state.useBboxHandles ? corners.length : corners.length - 1;
  const iHalf = Math.floor(n / 2);

  const rotateCenters = [];
  const headings = [];

  for (let i1 = 0; i1 < n; i1++) {
    let i0 = i1 - 1;
    if (i0 < 0) i0 += n;

    const c0 = corners[i0];
    const c1 = corners[i1];
    const rotPoint = midpoint(point(c0), point(c1));

    let rotCenter = center0;
    if (SRCenter.Opposite === state.rotatePivot) {
      const i3 = (i1 + iHalf) % n; // opposite corner
      var i2 = i3 - 1;
      if (i2 < 0) i2 += n;

      const c2 = corners[i2];
      const c3 = corners[i3];
      rotCenter = midpoint(point(c2), point(c3));
    }

    rotateCenters[i1] = rotCenter.geometry.coordinates;
    headings[i1] = bearing(rotCenter, rotPoint);
  }

  state.rotation = {
    feature0: polygon, // initial feature state
    centers: rotateCenters,
    headings, // rotation start heading for each point
  };

  // compute current distances from centers for scaling

  const scaleCenters = [];
  const distances = [];
  for (let i = 0; i < n; i++) {
    const c1 = corners[i];
    let c0 = center0.geometry.coordinates;
    if (SRCenter.Opposite === state.scaleCenter) {
      var i2 = (i + iHalf) % n; // opposite corner
      c0 = corners[i2];
    }
    scaleCenters[i] = c0;
    distances[i] = distance(point(c0), point(c1), { units: 'meters' });
  }

  state.scaling = {
    feature0: polygon, // initial feature state
    centers: scaleCenters,
    distances,
  };
};

SRMode.onDrag = function (state: drawState, e:any) {
  if (state.canDragMove !== true) return;
  state.dragMoving = true;
  e.originalEvent.stopPropagation();

  const delta = {
    lng: e.lngLat.lng - state.dragMoveLocation.lng,
    lat: e.lngLat.lat - state.dragMoveLocation.lat,
  };
  if (state.selectedCoordPaths.length > 0 && state.txMode) {
    switch (state.txMode) {
      case TxMode.Rotate:
        this.dragRotatePoint(state, e, delta);
        break;
      case TxMode.Scale:
        this.dragScalePoint(state, e, delta);
        break;
    }
  } else {
    this.dragFeature(state, e, delta);
  }

  state.dragMoveLocation = e.lngLat;
};

SRMode.dragRotatePoint = function (state:drawState, e:any, delta:any) {
  if (!state.rotation) {
    throw new Error('state.rotation required');
  }

  const polygon = state.feature.toGeoJSON();
  const m1 = point([e.lngLat.lng, e.lngLat.lat]);

  const n = state.rotation.centers.length;
  const cIdx = (this.coordinateIndex(state.selectedCoordPaths) + 1) % n;
  // TODO validate cIdx
  const cCenter = state.rotation.centers[cIdx];
  const center = point(cCenter);

  const heading1 = bearing(center, m1);

  const heading0 = state.rotation.headings[cIdx];
  let rotateAngle = heading1 - heading0; // in degrees
  if (CommonSelectors.isShiftDown(e)) {
    rotateAngle = 5.0 * Math.round(rotateAngle / 5.0);
  }

  const rotatedFeature = transformRotate(state.rotation.feature0, rotateAngle, {
    pivot: center,
    mutate: false,
  });

  state.feature.incomingCoords(rotatedFeature.geometry.coordinates);
  // TODO add option for this:
  this.fireUpdate();
};

SRMode.dragScalePoint = function (state:drawState, e:any, delta:any) {
  if (!state.scaling) {
    throw new Error('state.scaling required');
  }

  const polygon = state.feature.toGeoJSON();

  const cIdx = this.coordinateIndex(state.selectedCoordPaths);
  // TODO validate cIdx

  const cCenter = state.scaling.centers[cIdx];
  const center = point(cCenter);
  const m1 = point([e.lngLat.lng, e.lngLat.lat]);

  const dist = distance(center, m1, { units: 'meters' });
  const baseDist = state.scaling.distances[cIdx];
  if (!Number.isFinite(baseDist) || baseDist <= 0) return;

  let scale = dist / baseDist;
  if (!Number.isFinite(scale) || scale <= 0) return;

  if (CommonSelectors.isShiftDown(e)) {
    // TODO discrete scaling
    scale = 0.05 * Math.round(scale / 0.05);
  }

  const scaledFeature = transformScale(state.scaling.feature0, scale, {
    origin: cCenter,
    mutate: false,
  });

  state.feature.incomingCoords(scaledFeature.geometry.coordinates);
  // TODO add option for this:
  this.fireUpdate();
};

SRMode.dragFeature = function (state:drawState, e:any, delta:any) {
  moveFeatures(this.getSelected(), delta);
  state.dragMoveLocation = e.lngLat;
  // TODO add option for this:
  this.fireUpdate();
};

SRMode.fireUpdate = function () {
  this.map.fire(Constants.events.UPDATE, {
    action: Constants.updateActions.CHANGE_COORDINATES,
    features: this.getSelected().map((f:any) => f.toGeoJSON()),
  });
};

SRMode.onMouseOut = function (state:drawState) {
  // As soon as you mouse leaves the canvas, update the feature
  if (state.dragMoving) {
    this.fireUpdate();
  }
};

SRMode.onTouchEnd = SRMode.onMouseUp = function (state:drawState) {
  if (state.dragMoving) {
    this.fireUpdate();
  }
  this.stopDragging(state);
};

SRMode.clickActiveFeature = function (state:drawState) {
  state.selectedCoordPaths = [];
  this.clearSelectedCoordinates();
  state.feature.changed();
};

SRMode.onClick = function (state:drawState, e:any) {
  if (CommonSelectors.noTarget(e)) return this.clickNoTarget(state, e);
  if (CommonSelectors.isActiveFeature(e)) return this.clickActiveFeature(state, e);
  if (CommonSelectors.isInactiveFeature(e)) return this.clickInactive(state, e);
  this.stopDragging(state);
};

SRMode.clickNoTarget = function (state:drawState, e:any) {
  // document.getElementsByClassName('geobox-gl-rotate-icon')[0].checked = false;
  if (state.canSelectFeatures) this.changeMode(Constants.modes.SIMPLE_SELECT);
};

SRMode.clickInactive = function (state:drawState, e:any) {
  if (state.canSelectFeatures) {
    this.changeMode(Constants.modes.SIMPLE_SELECT, {
      featureIds: [e.featureTarget.properties.id],
    });
  }
};

SRMode.onTrash = function () {
  this.deleteFeature(this.getSelectedIds());
  // this.fireActionable();
};


export default SRMode;